import { Component, EventEmitter, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { faArrowDown, faArrowUp, faCopy, faPlusCircle, faSearch, faSort, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject, first } from 'rxjs';
import { UserService } from 'src/app/core/services/user.service';
import { indicate } from 'src/app/operators';
import { MultiSelectFilterComponent } from 'src/app/search/filters/multi-select/multi-select-filter/multi-select-filter.component';

import { EsraPipingAPIClient, PipeClassViewModel, PipingRevisionViewModel, RevisionStatus, SimpleRevisionViewModel, ValveViewModel } from 'src/app/shared/models/autogenerated-piping';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { CreateRevisionComponent } from '../../revisions/create-revision/create-revision.component';
import { TechModCreateComponent } from '../../techmod-create/techmod-create.component';
import { ValveSpecModalComponent, ValveSpecModalData } from 'src/app/search/valve-spec-modal/valve-spec-modal.component';
import { ClonePipeClassDialogComponent, ClonePipeClassDialogData } from 'src/app/search/clone-pipe-class-dialog/clone-pipe-class-dialog.component';

class Filter {
  filterName: string | null;
  options: string[];

  constructor(filterName: string, options: string[]) {
    this.filterName = filterName;
    this.options = options;
  }
}

class TableObject {
  revisionId: number;
  title?: string;
  componentId?: string;
  authorName?: string;
  status?: RevisionStatus;
  bu?: string;
  application: string;
  component?: string;
  publishDate?: Date; // This is being used as creation date for revisions
  isMoreThanOneDayOld?: boolean;
  editRoute?: string;
}

@Component({
    selector: 'app-revisions-tab',
    templateUrl: './revisions-tab.component.html',
    styleUrls: ['./revisions-tab.component.scss'],
    standalone: false
})
export class RevisionsTabComponent implements OnInit {
  // ── Icons ─────────────────────────────────────────────────────────────
  readonly faSearch = faSearch;
  readonly faPlusCircle = faPlusCircle;
  readonly faSort = faSort;
  readonly faTrashCan = faTrashCan;
  readonly faCopy = faCopy;
  readonly faArrowDown = faArrowDown;
  readonly faArrowUp = faArrowUp;

  // ── State ─────────────────────────────────────────────────────────────
  loadingIndicator$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  revisions: Array<PipingRevisionViewModel> = [];
  checkedItems: PipingRevisionViewModel[] = [];

  tableObjects: Array<TableObject> = [];
  dataSource: MatTableDataSource<TableObject> = new MatTableDataSource<TableObject>([]);

  selectAllText: string = 'select all';
  columnNames: string[] = ['statusMarker', 'title', 'new', 'author', 'status', 'bu', 'application', 'component', 'trashCan'];

  revisionsToDelete: TableObject[] = [];
  canBulkDelete: boolean = false;

  // ── Type filter (pipe / valve) ─────────────────────────────────────────
  showPipes: boolean = true;
  showValves: boolean = true;
  /** Master list — never mutated after initial load. */
  private allTableObjects: TableObject[] = [];
  /** Post-filter list; sliced for pagination. */
  private filteredTableObjects: TableObject[] = [];

  // ── Filters ───────────────────────────────────────────────────────────
  filters: Filter[] = [];
  /**
   * Tracks the currently selected options for each sidebar filter.
   * Keys are filter names (e.g. 'Status', 'BU'); values are the selected option sets.
   */
  private filterSelections: Map<string, Set<string>> = new Map<string, Set<string>>();
  searchText: string = '';

  // ── Status bar counts ─────────────────────────────────────────────────
  numOfRevisionsInDraft: number = 0;
  numOfRevisionsUnderReview: number = 0;
  numOfRevisionsReviewRejected: number = 0;
  numOfRevisionsReviewPending: number = 0;
  numOfRevisionsReadyForPublish: number = 0;
  numOfRevisionsApprovalPending: number = 0;

  draftFraction: number = 0;
  reviewFraction: number = 0;
  reviewRejectedFraction: number = 0;
  reviewPendingFraction: number = 0;
  readyForPublishFraction: number = 0;
  approvalPendingFraction: number = 0;

  get draftFractionPercent(): string { return `${this.draftFraction}%`; }
  get reviewFractionPercent(): string { return `${this.reviewFraction}%`; }
  get reviewRejectedFractionPercent(): string { return `${this.reviewRejectedFraction}%`; }
  get reviewPendingFractionPercent(): string { return `${this.reviewPendingFraction}%`; }
  get readyForPublishFractionPercent(): string { return `${this.readyForPublishFraction}%`; }
  get approvalPendingFractionPercent(): string { return `${this.approvalPendingFraction}%`; }

  // ── Sort state ────────────────────────────────────────────────────────
  /** Column currently driving the sort order. Defaults to publish date descending. */
  activeSortColumn: string = 'publishDate';
  /** When true, the active column sorts ascending; false = descending (default for dates). */
  sortAscending: boolean = false;

  // ── Pagination ────────────────────────────────────────────────────────
  paginatorTotalCount: number = 0;
  currentPage: number = 0;
  pageSize: number = 50;
  pageSizes: number[] = [2, 5, 10, 25, 50, 100];

  allRevisionsSelected: boolean = false;
  showIssueRevisionsButton: boolean = true;
  userIsAdmin: boolean = false;

  /** Keys of rows currently checked, used to restore checkbox state across page changes. */
  private selectedRowKeys: Set<string> = new Set<string>();

  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // ── Filter option lists ────────────────────────────────────────────────
  private titles: string[] = [];
  private authors: string[] = [];
  private statuses: number[] = [];
  private businessUnits: string[] = [];
  private applications: string[] = [];
  private components: string[] = [];

  @Output() checkedItem: EventEmitter<SimpleRevisionViewModel[]> = new EventEmitter<SimpleRevisionViewModel[]>();
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChildren(MultiSelectFilterComponent) childFilters: QueryList<MultiSelectFilterComponent>;

  constructor(
    private readonly esraPipingAPIClient: EsraPipingAPIClient,
    private readonly snackBarService: SnackBarService,
    private readonly dialog: MatDialog,
    private readonly userService: UserService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.esraPipingAPIClient.getUnpublishedRevisions()
      .pipe(indicate(this.loading$), first())
      .subscribe(revisions => {
        this.revisions = [...revisions];
        this.convertPipeClassesAndComponentsToTableObjects(revisions);
        this.allTableObjects = [...this.tableObjects];
        this.initializeUniqueRevisionObjectProperties(this.tableObjects);
        this.applyAllFilters();
      });
    this.userIsAdmin = this.userService.canPublishPipingAndValvesRevisions();
  }

  /**
   * Handles a change in any sidebar filter checkbox.
   * Receives the full set of currently selected values for the filter.
   */
  onCheckboxChange(selectedValues: Set<string>, filter: Filter): void {
    const filterName: string = filter.filterName ?? '';
    if (selectedValues.size === 0) {
      this.filterSelections.delete(filterName);
    } else {
      this.filterSelections.set(filterName, new Set<string>(selectedValues));
    }
    this.currentPage = 0;
    this.applyAllFilters();
  }

  clearAllFilters(): void {
    this.childFilters.forEach(childFilter => childFilter.clearSelections());
    this.filterSelections.clear();
    this.searchText = '';
    this.currentPage = 0;
    this.applyAllFilters();
  }

  callSearch(term: string): void {
    this.searchText = term;
    this.currentPage = 0;
    this.applyAllFilters();
  }

  /**
   * Applies all active filters (type toggle, sidebar checkboxes, free-text search) to
   * `allTableObjects`, sorts the result, updates the paginator count, and refreshes the
   * visible page. All filtering is client-side.
   */
  private applyAllFilters(): void {
    // Apply the pipe/valve type toggle first
    let result: TableObject[] = this.allTableObjects.filter(obj => {
      if (obj.component === 'pipe')  { return this.showPipes; }
      if (obj.component === 'valve') { return this.showValves; }
      return true;
    });

    // Apply each sidebar filter; only active (non-empty) selections narrow the set
    for (const [filterName, selectedValues] of this.filterSelections) {
      if (selectedValues.size === 0) { continue; }
      result = result.filter(obj => {
        switch (filterName) {
          case 'Title':       return selectedValues.has(obj.title ?? '');
          case 'Author':      return selectedValues.has(obj.authorName ?? '');
          case 'Status':      return selectedValues.has(this.convertStatusToString(obj.status ?? 0) ?? '');
          case 'BU':          return selectedValues.has(obj.bu ?? '');
          case 'Application': return selectedValues.has(obj.application ?? '');
          case 'Component':   return selectedValues.has(obj.component ?? '');
          default:            return true;
        }
      });
    }

    // Apply free-text search across visible fields
    if (this.searchText) {
      const term: string = this.searchText.toLowerCase().trim();
      result = result.filter(obj =>
        [obj.title, obj.authorName, this.convertStatusToString(obj.status ?? 0), obj.bu, obj.application, obj.component]
          .some(v => v?.toLowerCase().includes(term))
      );
    }

    this.filteredTableObjects = result;
    this.paginatorTotalCount = result.length;
    this.setNumOfStatuses(result);
    this.applySortToResult(this.filteredTableObjects);
    this.setPagedData();
  }

  /** Sorts `result` in-place according to `activeSortColumn` and `sortAscending`. */
  private applySortToResult(result: TableObject[]): void {
    const asc: boolean = this.sortAscending;
    const compareStr = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0) * (asc ? 1 : -1);
    const compareDate = (a: Date | undefined, b: Date | undefined): number => {
      const da: number = a?.getTime() ?? 0;
      const db: number = b?.getTime() ?? 0;
      return (da - db) * (asc ? 1 : -1);
    };

    switch (this.activeSortColumn) {
      case 'title':       result.sort((a, b) => compareStr(a.title ?? '', b.title ?? '')); break;
      case 'author':      result.sort((a, b) => compareStr(a.authorName ?? '', b.authorName ?? '')); break;
      case 'status':      result.sort((a, b) => ((a.status ?? 0) - (b.status ?? 0)) * (asc ? 1 : -1)); break;
      case 'bu':          result.sort((a, b) => compareStr(a.bu ?? '', b.bu ?? '')); break;
      case 'application': result.sort((a, b) => compareStr(a.application ?? '', b.application ?? '')); break;
      case 'component':   result.sort((a, b) => compareStr(a.component ?? '', b.component ?? '')); break;
      case 'publishDate': result.sort((a, b) => compareDate(a.publishDate, b.publishDate)); break;
    }
  }

  sortTable(columnName: string): void {
    // Toggle direction when re-clicking the active column; start ascending for a new column.
    if (this.activeSortColumn === columnName) {
      this.sortAscending = !this.sortAscending;
    } else {
      this.activeSortColumn = columnName;
      this.sortAscending = true;
    }
    this.currentPage = 0;
    this.applyAllFilters();
  }

  onTypeFilterChange(): void {
    this.currentPage = 0;
    this.applyAllFilters();
  }

  updatePage(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.setPagedData();
  }

  private setPagedData(): void {
    const start: number = this.currentPage * this.pageSize;
    const end: number = start + this.pageSize;
    this.dataSource = new MatTableDataSource<TableObject>(this.filteredTableObjects.slice(start, end));
  }

  convertPipeClassesAndComponentsToTableObjects(revisions: PipingRevisionViewModel[]): void {
    revisions.forEach((revision) => {
      if (revision.pipeClasses !== null) {
        revision.pipeClasses.forEach((pipeClass: PipeClassViewModel) => {
          const tableObject: TableObject = {
            revisionId: revision.id,
            title: pipeClass.name,
            componentId: pipeClass.id,
            // BE needs to be updated so the SimpleRevisionViewModels contain an author's displayName
            authorName: revision.authorName,
            status: revision.status,
            // businessUnits may be empty for a freshly cloned Draft that has not yet been assigned a BU.
            bu: pipeClass.businessUnits?.[0] ?? '',
            application: pipeClass.application ?? '',
            component: 'pipe',
            publishDate: revision.creationDate,
            isMoreThanOneDayOld: this.isRevisionLessThanOneDayOld(revision.creationDate),
            editRoute: `pipingsummary/`
          };
          this.tableObjects.push(tableObject);
        });
      }

      if (revision.valves !== null) {
        revision.valves.forEach((valve: ValveViewModel) => {
          const tableObject: TableObject = {
            revisionId: revision.id,
            title: valve.tag,
            componentId: valve.id,
            authorName: revision.authorName,
            status: revision.status,
            bu: valve.businessUnits?.[0] ?? '',
            application: valve.application ?? '',
            component: 'valve',
            publishDate: revision.publishDate,
            isMoreThanOneDayOld: this.isRevisionLessThanOneDayOld(revision.publishDate),
            editRoute: `valvesummary/`
          };
          this.tableObjects.push(tableObject);
        });
      }
    });
  }

  initializeUniqueRevisionObjectProperties(tableObjects: Array<TableObject>) {
    tableObjects.forEach(obj => {
      if (!this.titles.includes(obj.title) && obj.title != '' && obj.title != null) {
        this.titles.push(obj.title);
      }
      if (!this.authors.includes(obj.authorName) && obj.authorName != '' && obj.authorName != null) {
        this.authors.push(obj.authorName);
      }
      if (!this.statuses.includes(obj.status) && obj.status != null) {
        this.statuses.push(obj.status);
      }
      if (!this.businessUnits.includes(obj.bu) && obj.bu != '' && obj.bu != null) {
        this.businessUnits.push(obj.bu);
      }
      if (!this.applications.includes(obj.application) && obj.application != '' && obj.application != null) {
        this.applications.push(obj.application);
      }
      if (!this.components.includes(obj.component)) {
        this.components.push(obj.component);
      }
    });

    const uniqueStatusesAsStrings = [];
    for (const status of this.statuses) {
      uniqueStatusesAsStrings.push(this.convertStatusToString(status));
    }

    if (this.titles.length > 0) {
      this.filters.push(new Filter('Title', this.titles));
    }

    if (this.authors.length > 0) {
      this.filters.push(new Filter('Author', this.authors));
    }

    if (this.statuses.length > 0) {
      this.filters.push(new Filter('Status', uniqueStatusesAsStrings));
    }

    if (this.businessUnits.length > 0) {
      this.filters.push(new Filter('BU', this.businessUnits));
    }

    if (this.applications.length > 0) {
      this.filters.push(new Filter('Application', this.applications));
    }

    if (this.components.length > 0) {
      this.filters.push(new Filter('Component', this.components));
    }
  }

  setNumOfStatuses(objects: TableObject[]): void {
    // Reset before recounting so filter changes don't accumulate
    this.numOfRevisionsInDraft = 0;
    this.numOfRevisionsUnderReview = 0;
    this.numOfRevisionsReviewRejected = 0;
    this.numOfRevisionsReviewPending = 0;
    this.numOfRevisionsReadyForPublish = 0;
    this.numOfRevisionsApprovalPending = 0;

    for (const tableObject of objects) {
      switch (tableObject.status) {
        case 0:   this.numOfRevisionsInDraft++;           break;
        case 1:   this.numOfRevisionsUnderReview++;       break;
        case 10:  this.numOfRevisionsReviewRejected++;    break;
        case 11:  this.numOfRevisionsReviewPending++;     break;
        case 2:   this.numOfRevisionsReadyForPublish++;   break;
        case 12:  this.numOfRevisionsApprovalPending++;   break;
      }
    }

    const totalReviews: number = objects.length;

    if (totalReviews > 0) {
      this.draftFraction              = (this.numOfRevisionsInDraft          / totalReviews) * 100;
      this.reviewFraction             = (this.numOfRevisionsUnderReview      / totalReviews) * 100;
      this.reviewRejectedFraction     = (this.numOfRevisionsReviewRejected   / totalReviews) * 100;
      this.reviewPendingFraction      = (this.numOfRevisionsReviewPending    / totalReviews) * 100;
      this.readyForPublishFraction    = (this.numOfRevisionsReadyForPublish  / totalReviews) * 100;
      this.approvalPendingFraction    = (this.numOfRevisionsApprovalPending  / totalReviews) * 100;
    } else {
      this.draftFraction = 0;
      this.reviewFraction = 0;
      this.reviewRejectedFraction = 0;
      this.reviewPendingFraction = 0;
      this.readyForPublishFraction = 0;
      this.approvalPendingFraction = 0;
    }
  }

  markRevisionForDeletion(event: Event, revision: TableObject): void {
    const isChecked: boolean = (event.target as HTMLInputElement).checked;
    const key: string = this.rowKey(revision);

    if (isChecked) {
      this.revisionsToDelete.push(revision);
      this.selectedRowKeys.add(key);
    }

    if (!isChecked) {
      this.removeFromArray(this.revisionsToDelete, revision);
      this.selectedRowKeys.delete(key);
    }

    this.canBulkDelete = this.revisionsToDelete.some(r => this.isDeletableByCurrentUser(r));
  }

  selectRevision(event: Event, revisionId: number): void {
    const isChecked: boolean = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      const selectedItem: PipingRevisionViewModel = this.revisions.find(revision => revision.id === revisionId);
      this.checkedItems.push(selectedItem);
    } else {
      this.checkedItems = this.checkedItems.filter(revision => revision.id !== revisionId);
    }
  }

  /** Returns the CSS class for the left-border status marker on a table row. */
  getStatusRowClass(statusNum: number): string {
    if (statusNum === 0)   return 'status-row-draft';
    if (statusNum === 1)   return 'status-row-review';
    if (statusNum === 10)  return 'status-row-review-rejected';
    if (statusNum === 11)  return 'status-row-review-pending';
    if (statusNum === 2)   return 'status-row-ready';
    if (statusNum === 12)  return 'status-row-approval-pending';
    if (statusNum === 3)   return 'status-row-published';
    return '';
  }

  convertStatusToString(statusNum: number): string {
    if (statusNum === 0)   return 'Draft';
    if (statusNum === 1)   return 'In Review';
    if (statusNum === 10)  return 'In Review (Contains Rejections)';
    if (statusNum === 11)  return 'In Review (Pending Comments)';
    if (statusNum === 2)   return 'Ready to Publish';
    if (statusNum === 12)  return 'Ready (Pending Comments)';
    if (statusNum === 3)   return 'Published';
    if (statusNum === 100) return 'Retired';
    if (statusNum === 101) { return 'Rejected'; }
    return '';
  }

  createNewRevision(): void {
    this.dialog.open(CreateRevisionComponent, {
      width: '90%',
      height: '90%'
    });
  }

  openIssueRevisionsDialog() {
    if (this.checkedItems.length < 1) {
      this.snackBarService.showSnackBar(
        true,
        'You have not selected any revisions to issue',
        null,
        null,
        2000,
        'center',
        'bottom'
      );
      return;
    }

    if (this.userService.canPublishPipingAndValvesRevisions()) {
      const dialogRef = this.dialog.open(TechModCreateComponent, {
        width: '65%',
        maxHeight: '80%',
        data: { selectedItems: this.checkedItems }
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result === 'reload') {
          location.reload();
        }
      });
    } else {
      this.snackBarService.showError('You are not authorized to issue revisions');
    }
  }

  deleteRevision(revisionToDelete: TableObject) {
    if (window.confirm(`Are you sure you want to delete revision ${revisionToDelete.title}?`)) {
      this.esraPipingAPIClient.deleteRevision(revisionToDelete.revisionId)
        .subscribe(() => {
          location.reload();
        });
    }
  }

  openCloneDraftDialog(revision: TableObject): void {
    const dialogData: ValveSpecModalData = {
      mode: 'cloneDraft',
      valveId: revision.componentId,
      valveTag: revision.title,
      // Passed so the release lookup can fall back to the source application when the
      // user does not override the application on the clone.
      sourceApplication: revision.application
    };

    this.dialog.open(ValveSpecModalComponent, {
      width: '650px',
      disableClose: false,
      data: dialogData
    });
  }

  /**
   * Opens the Clone PipeClass dialog for a pipe class row in the revisions dashboard.
   * Pre-fills Business Unit and Application from the revision table row data.
   * Shows the draft info message because the clone will not be attached to the current revision.
   */
  openClonePipeClassDraftDialog(revision: TableObject): void {
    const dialogData: ClonePipeClassDialogData = {
      sourcePipeClassId:   revision.componentId,
      prefillBusinessUnit: revision.bu,
      prefillApplication:  revision.application,
      // Inform the user that the clone is a standalone Draft, unrelated to the current release/revision.
      showDraftInfoMessage: true,
      // Tell the dialog to restore the revision dashboard as the back destination after a successful clone.
      returnUrl: '/piping/revisionsAndReleases',
    };

    this.dialog.open(ClonePipeClassDialogComponent, {
      width: '650px',
      disableClose: false,
      data: dialogData,
    });
  }

  deleteRevisions(revisionsToDelete: TableObject[]): void {
    const deletable: TableObject[] = revisionsToDelete.filter(r => this.isDeletableByCurrentUser(r));

    if (deletable.length === 0) {
      this.snackBarService.showInfo('None of the selected items can be deleted. Only your own Draft items are eligible.');
      return;
    }

    const names: string = deletable.map(r => r.title ?? '').join(', ');
    const skipped: number = revisionsToDelete.length - deletable.length;
    const skipNote: string = skipped > 0 ? `\n\n${skipped} non-deletable item(s) will be skipped.` : '';

    if (window.confirm(`Delete ${deletable.length} revision(s)?${skipNote}\n\n${names}`)) {
      for (const revision of deletable) {
        this.esraPipingAPIClient.deleteRevision(revision.revisionId).subscribe(() => {
          location.reload();
        });
      }
    }
  }

  selectAllRevisions(): void {
    if (!this.allRevisionsSelected) {
      this.filteredTableObjects.forEach((tableObj) => {
        const revisionToAdd = this.revisions.find(revision => revision.id === tableObj.revisionId);
        if (
          revisionToAdd
          && !this.checkedItems.some(item => item.id === revisionToAdd.id)
          && (revisionToAdd.pipeClasses.length > 0 || revisionToAdd.components.length > 0)
        ) {
          this.checkedItems.push(revisionToAdd);
        }
        if (!this.selectedRowKeys.has(this.rowKey(tableObj))) {
          this.revisionsToDelete.push(tableObj);
          this.selectedRowKeys.add(this.rowKey(tableObj));
        }
      });

      this.selectAllText = 'deselect all';
      this.canBulkDelete = this.revisionsToDelete.some(r => this.isDeletableByCurrentUser(r));
    } else {
      this.checkedItems = [];
      this.revisionsToDelete = [];
      this.selectedRowKeys.clear();
      this.canBulkDelete = false;
      this.selectAllText = 'select all';
    }

    this.allRevisionsSelected = !this.allRevisionsSelected;
  }

  /** True when this row's checkbox should appear checked (persists across page changes). */
  isRowSelected(revision: TableObject): boolean {
    return this.selectedRowKeys.has(this.rowKey(revision));
  }

  private rowKey(revision: TableObject): string {
    return `${revision.revisionId}-${revision.componentId ?? ''}`;
  }

  /** Returns true when the current user can delete this row: must be Draft status and authored by the current user. */
  private isDeletableByCurrentUser(obj: TableObject): boolean {
    return obj.status === 0 && this.isUserTheAuthor(obj.authorName ?? '');
  }

  private removeFromArray(array: unknown[], element: unknown): void {
    const removalIndex: number = array.indexOf(element);
    if (removalIndex !== -1) {
      array.splice(removalIndex, 1);
    }
  }

  isRevisionLessThanOneDayOld(publishDate: Date): boolean {
    // Show the NEW badge for 12 hours after creation (half a day), so users
    // can easily spot recent revisions without the tag lingering until midnight.
    const MS_IN_HALF_DAY: number = 1000 * 60 * 60 * 12;
    const currentDate: Date = new Date();
    const timeDifference: number = currentDate.getTime() - publishDate.getTime();
    return timeDifference < MS_IN_HALF_DAY;
  }

  /**
   * Checks if the logged-in user is the author of the revision.
   * @param authorName The display name of the revision author.
   * @returns True if the current user's name matches the author name, false otherwise.
   */
  isUserTheAuthor(authorName: string): boolean {
    const currentUser = this.userService.getCurrentUser();
    if (!currentUser || !authorName) {
      return false;
    }
    return currentUser.name === authorName;
  }

  navigateToSummary(revision: TableObject): void {
    if (revision.component === 'valve') {
      // Go directly to the valve edit page
      this.router.navigate(['/piping/valve/edit'], {
        queryParams: { mode: 'edit', id: revision.componentId }
      });
      return;
    }
    // Pipe classes: open the piping summary in edit mode.
    // Pass the return URL via router state so the back button in piping-tabs-parent
    // navigates here when the user clicks return.
    this.router.navigate([revision.editRoute, revision.componentId], {
      queryParams: { isEditMode: 'true' },
      state: { returnUrl: '/piping/revisionsAndReleases' }
    });
  }
}
